import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  mainPageSteps,
  kundenSteps,
  inventorySteps,
  eingangslisteSteps,
  inventoryBestandSteps,
  uebersichtSteps,
  geschenkSteps,
} from '../utils/tourSteps';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const InteractiveTour = ({ user, onComplete, manualStart = false }) => {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [currentPage, setCurrentPage] = useState('main'); // main, kunden, inventory, eingangsliste, uebersicht, inventory-bestand, geschenk
  const [showWelcome, setShowWelcome] = useState(false);
  const [showChoice, setShowChoice] = useState(false);
  const [spotlightTarget, setSpotlightTarget] = useState(null);
  const [spotlightKey, setSpotlightKey] = useState(0); // Für Force-Rerender
  const [showHeaderHint, setShowHeaderHint] = useState(false); // Für Header-Modal
  const [showSkipHint, setShowSkipHint] = useState(false); // Für Abbruch-Modal mit Info-Button Hinweis
  const [actionCompleted, setActionCompleted] = useState(false); // Track if required action is done
  const [inventoryGlobalStep, setInventoryGlobalStep] = useState(0); // Globaler Step-Counter für Inventory-Bereich
  const [highlightedColumns, setHighlightedColumns] = useState([]); // Für Spalten-Highlight
  const [datePickerOpen, setDatePickerOpen] = useState(false); // Für dynamisches Spotlight bei Datums-Feldern
  const [dateValidationError, setDateValidationError] = useState(false); // Für Datumsvalidierung (wackelnder Button + roter Text)
  const navigate = useNavigate();
  const location = useLocation();

  // Globaler Cleanup für Scroll-Blocking bei Route-Änderungen
  useEffect(() => {
    // Entferne Scroll-Blocking bei jeder Route-Änderung
    const resetScrollBlocking = () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
    
    // Wenn showHeaderHint NICHT aktiv ist, immer Scroll-Blocking entfernen
    if (!showHeaderHint) {
      resetScrollBlocking();
    }
    
    // Cleanup bei Unmount
    return () => {
      resetScrollBlocking();
    };
  }, [location.pathname, showHeaderHint]);

  // Box-Shadow auf die 3 Haupt-Karten setzen wenn letzter Schritt aktiv
  const MAIN_TOUR_SELECTORS = ['[data-tour="kunden-button"]', '[data-tour="zielplanung-button"]', '[data-tour="inventory-button"]'];
  useEffect(() => {
    if (showHeaderHint) {
      // Kurze Verzögerung damit Scroll erst abläuft
      setTimeout(() => {
        MAIN_TOUR_SELECTORS.forEach(sel => {
          const el = document.querySelector(sel);
          if (el) {
            el.style.boxShadow = '0 0 0 3px #34d399, 0 0 20px rgba(52,211,153,0.45)';
            el.style.borderRadius = '12px';
            el.style.transition = 'box-shadow 0.3s';
          }
        });
      }, 400);
    } else {
      // Cleanup: Box-Shadow entfernen
      MAIN_TOUR_SELECTORS.forEach(sel => {
        const el = document.querySelector(sel);
        if (el) {
          el.style.boxShadow = '';
          el.style.transition = '';
        }
      });
    }
  }, [showHeaderHint]); // box-shadow effect for final tour step

  // (multiSpotlight übernimmt Visualisierung für Uhrzeit- und Anderes-Datum-Schritte)

  // Tour beim ersten Login starten (nur wenn noch nicht completed)
  useEffect(() => {
    if (user && !user.tour_completed && location.pathname === '/main-hub' && !manualStart) {
      setShowWelcome(true);
    }
  }, [user, location.pathname, manualStart]);
  
  // Manuelle Tour starten - erkenne die Seite
  useEffect(() => {
    if (manualStart) {
      // Reset alle States
      setIsActive(false);
      setShowChoice(false);
      setShowHeaderHint(false);
      setCurrentStep(0);
      
      // Erkenne Seite und setze currentPage
      if (location.pathname.includes('/kunden')) {
        setCurrentPage('kunden');
      } else if (location.pathname.includes('/eingangsliste')) {
        setCurrentPage('eingangsliste');
      } else if (location.pathname.includes('/dashboard')) {
        setCurrentPage('inventory');
      } else {
        setCurrentPage('main');
      }
      
      // Dann Welcome Modal zeigen
      setTimeout(() => {
        setShowWelcome(true);
      }, 50);
    }
  }, [manualStart, location.pathname]);

  // Funktion um Tour aus localStorage zu starten
  const checkAndStartTourFromStorage = () => {
    const storedTour = localStorage.getItem('burningTeamTour');
    console.log('🔍 checkAndStartTourFromStorage called - pathname:', location.pathname, 'storedTour:', storedTour);
    
    let tourData = null;
    
    if (storedTour) {
      try {
        tourData = JSON.parse(storedTour);
        console.log('🔍 Tour Check - pathname:', location.pathname, 'localStorage parsed:', tourData);
      } catch (e) {
        console.error('Error parsing localStorage tour data', e);
        return false;
      }
    } else if (window.burningTeamTour) {
      tourData = window.burningTeamTour;
      console.log('🔍 Tour Check - pathname:', location.pathname, 'window.burningTeamTour:', tourData);
    } else {
      console.log('🔍 No tour data found in localStorage or window');
      return false;
    }
    
    // Prüfe ob Tour gestartet werden soll
    if (tourData && tourData.shouldStart === true) {
      const tourPage = tourData.page;
      const globalStep = tourData.globalStep || 0;
      
      // Prüfe ob wir auf der richtigen Seite sind
      const isKundenPage = location.pathname.includes('/kunden') && tourPage === 'kunden';
      const isInventoryPage = location.pathname.includes('/dashboard') && tourPage === 'inventory';
      const isInventoryBestandPage = location.pathname.includes('/dashboard') && tourPage === 'inventory-bestand';
      const isEingangslistePage = location.pathname.includes('/eingangsliste') && tourPage === 'eingangsliste';
      const isUebersichtPage = location.pathname.includes('/uebersicht') && tourPage === 'uebersicht';
      const isGeschenkPage = location.pathname.includes('/dashboard') && tourPage === 'geschenk';
      
      console.log('📍 Page Check:', { isKundenPage, isInventoryPage, isInventoryBestandPage, isEingangslistePage, isUebersichtPage, isGeschenkPage, tourPage, globalStep, pathname: location.pathname });
      
      if (isKundenPage || isInventoryPage || isInventoryBestandPage || isEingangslistePage || isUebersichtPage || isGeschenkPage) {
        console.log('🎯 Tour Start via localStorage/window:', tourPage, location.pathname, 'globalStep:', globalStep);
        
        // Cleanup: Variable löschen (sowohl window als auch localStorage)
        window.burningTeamTour = null;
        localStorage.removeItem('burningTeamTour');
        
        // Setze Page und starte Tour
        if (tourPage === 'inventory-bestand') {
          setCurrentPage('inventory-bestand');
        } else if (tourPage === 'uebersicht') {
          setCurrentPage('uebersicht');
        } else if (tourPage === 'geschenk') {
          setCurrentPage('geschenk');
        } else {
          setCurrentPage(tourPage);
        }
        setCurrentStep(0);
        
        // Setze globalen Step-Counter für Inventory-Bereich
        if (globalStep > 0) {
          setInventoryGlobalStep(globalStep);
        }
        
        // Warte kurz, dann Tour starten
        setTimeout(() => {
          console.log('✅ Tour wird gestartet! Step:', 0, 'globalStep:', globalStep);
          
          // Für Kunden-Bereich und Inventory-Bereich: Zeige Welcome-Modal statt direkt die Tour zu starten
          if (tourPage === 'kunden' || tourPage === 'inventory') {
            setShowWelcome(true);
          } else {
            setIsActive(true);
          }
          // Setze globale Variable für Dashboard onClick-Handler
          window.burningTeamTourActive = true;
          window.dispatchEvent(new CustomEvent('bt-tour-start'));
        }, 600);
        
        return true;
      }
    }
    return false;
  };

  // Tour auf Unterseiten starten (via window-Variable oder localStorage)
  useEffect(() => {
    checkAndStartTourFromStorage();
  }, [location.pathname]);
  
  // Zusätzlicher Check beim ersten Mount - mehrere Versuche für langsame Mobile/Cold-Start
  useEffect(() => {
    // Sofort prüfen
    let found = checkAndStartTourFromStorage();
    if (found) return;
    
    // Mehrere Versuche mit steigenden Verzögerungen (für Render Cold-Start + langsames Mobile)
    const delays = [500, 1000, 2000, 3000, 5000];
    const timers = [];
    
    delays.forEach((delay) => {
      const timer = setTimeout(() => {
        if (!isActive) { // Nur prüfen wenn Tour noch nicht aktiv
          console.log(`🔄 Tour Check nach ${delay}ms...`);
          checkAndStartTourFromStorage();
        }
      }, delay);
      timers.push(timer);
    });
    
    return () => timers.forEach(t => clearTimeout(t));
  }, []);

  // Manuelle Tour starten - erkenne die Seite
  useEffect(() => {
    if (manualStart) {
      // Reset alle States
      setIsActive(false);
      setShowChoice(false);
      setShowHeaderHint(false);
      setCurrentStep(0);
      
      // Erkenne Seite und setze currentPage
      if (location.pathname.includes('/kunden')) {
        setCurrentPage('kunden');
      } else if (location.pathname.includes('/eingangsliste')) {
        setCurrentPage('eingangsliste');
      } else if (location.pathname.includes('/dashboard')) {
        setCurrentPage('inventory');
      } else {
        setCurrentPage('main');
      }
      
      // Dann Welcome Modal zeigen
      setTimeout(() => {
        setShowWelcome(true);
      }, 50);
    }
  }, [manualStart, location.pathname]);


  const getTourSteps = () => {
    if (currentPage === 'main') return mainPageSteps;
    if (currentPage === 'kunden') return kundenSteps;
    if (currentPage === 'inventory') return inventorySteps;
    if (currentPage === 'uebersicht') return uebersichtSteps;
    if (currentPage === 'inventory-bestand') return inventoryBestandSteps;
    if (currentPage === 'eingangsliste') return eingangslisteSteps;
    if (currentPage === 'geschenk') return geschenkSteps;
    return [];
  };

  // Event-Listener für Klicks auf Kunden/Inventory Buttons während der Tour (Main-Hub)
  useEffect(() => {
    // Aktiviere Event-Listener wenn Tour aktiv ist ODER Header-Hint angezeigt wird
    if ((!isActive && !showHeaderHint) || currentPage !== 'main') return;
    
    const handleKundenClick = (e) => {
      // Tour soll auf Kunden-Seite weitergehen
      localStorage.setItem('burningTeamTour', JSON.stringify({
        shouldStart: true,
        page: 'kunden'
      }));
    };
    
    const handleInventoryClick = (e) => {
      // Tour soll auf Inventory-Seite weitergehen
      localStorage.setItem('burningTeamTour', JSON.stringify({
        shouldStart: true,
        page: 'inventory'
      }));
    };

    const handleZielplanungClick = () => {
      // Zielkalender-Onboarding beim Klick auf Zielplanung-Button starten
      localStorage.setItem('burningTeamTour', JSON.stringify({
        shouldStart: true,
        page: 'zielplanung'
      }));
    };
    
    const kundenButton = document.querySelector('[data-tour="kunden-button"]');
    const inventoryButton = document.querySelector('[data-tour="inventory-button"]');
    const zielplanungButton = document.querySelector('[data-tour="zielplanung-button"]');
    
    if (kundenButton) {
      kundenButton.addEventListener('click', handleKundenClick, { capture: true });
      kundenButton.addEventListener('touchend', handleKundenClick, { capture: true });
    }
    if (inventoryButton) {
      inventoryButton.addEventListener('click', handleInventoryClick, { capture: true });
      inventoryButton.addEventListener('touchend', handleInventoryClick, { capture: true });
    }
    if (zielplanungButton) {
      zielplanungButton.addEventListener('click', handleZielplanungClick, { capture: true });
      zielplanungButton.addEventListener('touchend', handleZielplanungClick, { capture: true });
    }
    
    return () => {
      if (kundenButton) {
        kundenButton.removeEventListener('click', handleKundenClick, { capture: true });
        kundenButton.removeEventListener('touchend', handleKundenClick, { capture: true });
      }
      if (inventoryButton) {
        inventoryButton.removeEventListener('click', handleInventoryClick, { capture: true });
        inventoryButton.removeEventListener('touchend', handleInventoryClick, { capture: true });
      }
      if (zielplanungButton) {
        zielplanungButton.removeEventListener('click', handleZielplanungClick, { capture: true });
        zielplanungButton.removeEventListener('touchend', handleZielplanungClick, { capture: true });
      }
    };
  }, [isActive, currentPage, showHeaderHint]);
  
  // Event-Listener für Navigation-Buttons auf Inventory-Seite
  useEffect(() => {
    if (!isActive || currentPage !== 'inventory') return;
    
    const handleEingangslisteClick = (e) => {
      console.log('🎯 Eingangsliste-Button geklickt - setze Tour-Variable');
      // Tour soll auf Eingangsliste-Seite weitergehen
      localStorage.setItem('burningTeamTour', JSON.stringify({
        shouldStart: true,
        page: 'eingangsliste',
        globalStep: 3 // Nach Welcome (1), Preisliste (2), Eingangsliste-Button (3)
      }));
    };
    
    const eingangslisteButton = document.querySelector('[data-tour="eingangsliste-button"]');
    
    if (eingangslisteButton) {
      eingangslisteButton.addEventListener('click', handleEingangslisteClick, { capture: true });
      eingangslisteButton.addEventListener('touchend', handleEingangslisteClick, { capture: true });
    }
    
    return () => {
      if (eingangslisteButton) {
        eingangslisteButton.removeEventListener('click', handleEingangslisteClick, { capture: true });
        eingangslisteButton.removeEventListener('touchend', handleEingangslisteClick, { capture: true });
      }
    };
  }, [isActive, currentPage]);

  // Felder während der Tour deaktivieren (Typ und Termin) - aber nicht wenn der aktuelle Schritt das Feld benötigt
  useEffect(() => {
    if (!isActive || currentPage !== 'kunden') return;
    
    const currentStepData = getTourSteps()[currentStep];
    const isTerminStep = currentStepData?.target === '[data-field="naechster-termin"]';
    const isZuletztKontaktiertStep = currentStepData?.target === '[data-field="zuletzt-kontaktiert"]';
    
    const disableFields = () => {
      // Deaktiviere Typ-Select (immer, außer beim Typ-Schritt)
      const typSelect = document.querySelector('select#typ');
      if (typSelect && !typSelect.disabled) {
        typSelect.disabled = true;
        typSelect.style.opacity = '0.6';
        typSelect.style.cursor = 'not-allowed';
        typSelect.style.pointerEvents = 'none';
      }
      
      // Deaktiviere Termin-Input und Button - NICHT wenn wir beim Termin-Schritt sind
      if (!isTerminStep) {
        const terminInput = document.querySelector('input#naechster_termin');
        if (terminInput && !terminInput.disabled) {
          terminInput.disabled = true;
          terminInput.style.opacity = '0.6';
          terminInput.style.cursor = 'not-allowed';
          terminInput.readOnly = true;
        }
        
        // Deaktiviere auch den Termin-Button
        const terminParent = terminInput?.parentElement;
        if (terminParent) {
          const button = terminParent.querySelector('button');
          if (button) {
            button.disabled = true;
            button.style.opacity = '0.6';
            button.style.cursor = 'not-allowed';
            button.style.pointerEvents = 'none';
          }
        }
      }
      
      // Deaktiviere Zuletzt-Kontaktiert-Input - NICHT wenn wir beim Zuletzt-Kontaktiert-Schritt sind
      if (!isZuletztKontaktiertStep) {
        const zuletztInput = document.querySelector('input#letzter_termin');
        if (zuletztInput && !zuletztInput.disabled) {
          zuletztInput.disabled = true;
          zuletztInput.style.opacity = '0.6';
          zuletztInput.style.cursor = 'not-allowed';
          zuletztInput.readOnly = true;
        }
      }
    };
    
    const enableTerminField = () => {
      const terminInput = document.querySelector('input#naechster_termin');
      if (terminInput) {
        terminInput.disabled = false;
        terminInput.style.opacity = '1';
        terminInput.style.cursor = 'text';
        terminInput.readOnly = false;
      }
      
      const terminParent = terminInput?.parentElement;
      if (terminParent) {
        const button = terminParent.querySelector('button');
        if (button) {
          button.disabled = false;
          button.style.opacity = '1';
          button.style.cursor = 'pointer';
          button.style.pointerEvents = 'auto';
        }
      }
    };
    
    const enableZuletztField = () => {
      const zuletztInput = document.querySelector('input#letzter_termin');
      if (zuletztInput) {
        zuletztInput.disabled = false;
        zuletztInput.style.opacity = '1';
        zuletztInput.style.cursor = 'text';
        zuletztInput.readOnly = false;
      }
    };
    
    // Wenn wir beim Termin-Schritt sind, aktiviere das Feld
    if (isTerminStep) {
      enableTerminField();
    }
    
    // Wenn wir beim Zuletzt-Kontaktiert-Schritt sind, aktiviere das Feld
    if (isZuletztKontaktiertStep) {
      enableZuletztField();
    }
    
    // Initiales Deaktivieren
    disableFields();
    
    // MutationObserver für dynamische Änderungen
    const observer = new MutationObserver(disableFields);
    observer.observe(document.body, { childList: true, subtree: true });
    
    // Periodisch prüfen (als Fallback)
    const interval = setInterval(disableFields, 500);
    
    // Cleanup: Reaktiviere Felder wenn Tour beendet
    return () => {
      observer.disconnect();
      clearInterval(interval);
      
      const typSelect = document.querySelector('select#typ');
      if (typSelect) {
        typSelect.disabled = false;
        typSelect.style.opacity = '1';
        typSelect.style.cursor = 'pointer';
        typSelect.style.pointerEvents = 'auto';
      }
      
      const terminInput = document.querySelector('input#naechster_termin');
      if (terminInput) {
        terminInput.disabled = false;
        terminInput.style.opacity = '1';
        terminInput.style.cursor = 'text';
        terminInput.readOnly = false;
      }
      
      const terminParent = terminInput?.parentElement;
      if (terminParent) {
        const button = terminParent.querySelector('button');
        if (button) {
          button.disabled = false;
          button.style.opacity = '1';
          button.style.cursor = 'pointer';
          button.style.pointerEvents = 'auto';
        }
      }
      
      const zuletztInput = document.querySelector('input#letzter_termin');
      if (zuletztInput) {
        zuletztInput.disabled = false;
        zuletztInput.style.opacity = '1';
        zuletztInput.style.cursor = 'text';
        zuletztInput.readOnly = false;
      }
    };
  }, [isActive, currentPage, currentStep]);

  // Event-Listener für interaktive Aktionen im Kunden-Bereich
  useEffect(() => {
    if (!isActive || currentPage !== 'kunden') return;
    
    const step = getTourSteps()[currentStep];
    if (!step) return;
    
    // Reset actionCompleted and datePickerOpen when step changes
    setActionCompleted(false);
    setDatePickerOpen(false);
    setDateValidationError(false);

    // Einfache Scroll-Hilfsfunktion (Original-Verhalten wiederhergestellt)
    const scrollToFieldHelper = (element, offsetFromTop = 200) => {
      const rect = element.getBoundingClientRect();
      const targetPosition = window.pageYOffset + rect.top - offsetFromTop;
      window.scrollTo({ top: Math.max(0, targetPosition), behavior: 'smooth' });
    };
    
    // Step 2: Warte auf Klick auf "Kontakt hinzufügen" Button - gehe AUTOMATISCH weiter
    if (step.target === '[data-tour="kontakt-hinzufuegen-button"]' && step.requireClick) {
      const button = document.querySelector('[data-tour="kontakt-hinzufuegen-button"]');
      const handleClick = () => {
        // Warte kurz bis Formular geöffnet ist, dann AUTOMATISCH zum nächsten Schritt
        setTimeout(() => {
          setCurrentStep(currentStep + 1);
        }, 800);
      };
      if (button) {
        button.addEventListener('click', handleClick);
        return () => button.removeEventListener('click', handleClick);
      }
    }
    
    // Step 3: Warte auf Eingabe im Name-Feld
    if (step.target === '[data-field="name"]' && step.requireInput) {
      const input = document.querySelector('input[data-field="name"]');
      const handleInput = (e) => {
        if (e.target.value.trim().length >= 1) {
          setActionCompleted(true);
        } else {
          setActionCompleted(false);
        }
      };
      if (input) {
        // Scroll to input – Mobile-kompatibel
        setTimeout(() => scrollToFieldHelper(input, 200), 300);
        
        input.addEventListener('input', handleInput);
        // Check initial value
        if (input.value.trim().length >= 1) {
          setActionCompleted(true);
        }
        return () => input.removeEventListener('input', handleInput);
      }
    }
    
    // Step 7 (Schritt 5): Warte auf Eingabe im "Erster Termin" Feld (naechster-termin)
    // Validierung: Datum muss das HEUTIGE Datum sein (deutsche Zeit)
    if (step.target === '[data-field="naechster-termin"]' && step.requireInput) {
      
      // Hilfsfunktion: Prüfe ob Datum heute ist (deutsche Zeit)
      const isToday = (dateString) => {
        if (!dateString) return false;
        
        console.log('🔍 Tour Debug - Input dateString:', dateString);
        
        // Hole heutiges Datum in deutscher Zeitzone
        const now = new Date();
        const germanFormatter = new Intl.DateTimeFormat('de-DE', {
          timeZone: 'Europe/Berlin',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        });
        const todayParts = germanFormatter.formatToParts(now);
        const todayYear = parseInt(todayParts.find(p => p.type === 'year').value, 10);
        const todayMonth = parseInt(todayParts.find(p => p.type === 'month').value, 10);
        const todayDay = parseInt(todayParts.find(p => p.type === 'day').value, 10);
        
        console.log('🔍 Tour Debug - Heute (DE):', todayDay + '.' + todayMonth + '.' + todayYear);
        
        // Parse Input-Datum (kann verschiedene Formate haben)
        let inputYear, inputMonth, inputDay;
        
        if (dateString.includes('-')) {
          // Format: YYYY-MM-DD oder YYYY-MM-DDTHH:MM
          const datePart = dateString.split('T')[0];
          const parts = datePart.split('-');
          inputYear = parseInt(parts[0], 10);
          inputMonth = parseInt(parts[1], 10);
          inputDay = parseInt(parts[2], 10);
        } else if (dateString.includes('.')) {
          // Format: DD.MM.YYYY
          const parts = dateString.split('.');
          inputDay = parseInt(parts[0], 10);
          inputMonth = parseInt(parts[1], 10);
          inputYear = parseInt(parts[2], 10);
        } else {
          return false;
        }
        
        console.log('🔍 Tour Debug - Input geparst:', inputDay + '.' + inputMonth + '.' + inputYear);
        console.log('🔍 Tour Debug - Match:', inputYear === todayYear && inputMonth === todayMonth && inputDay === todayDay);
        
        return inputYear === todayYear && inputMonth === todayMonth && inputDay === todayDay;
      };
      
      const handleTerminInput = () => {
        // Input-Element bei jedem Check neu abfragen (falls sich DOM ändert)
        const terminInput = document.querySelector('input#naechster_termin');
        if (terminInput && terminInput.value) {
          // Validiere ob es das heutige Datum ist
          if (isToday(terminInput.value)) {
            setActionCompleted(true);
            setDateValidationError(false);
          } else {
            setActionCompleted(false);
            setDateValidationError(true);
          }
        } else {
          setActionCompleted(false);
          setDateValidationError(false);
        }
      };
      
      // Event-Listener für Klick auf das Feld (öffnet DatePicker)
      const handleFieldClick = () => {
        setDatePickerOpen(true);
      };
      
      const terminField = document.querySelector('[data-field="naechster-termin"]');
      if (terminField) {
        terminField.addEventListener('click', handleFieldClick);
      }
      
      // Prüfe initial
      handleTerminInput();
      
      // Periodisch prüfen (alle 300ms für schnellere Reaktion)
      const checkInterval = setInterval(handleTerminInput, 300);
      
      return () => {
        if (terminField) {
          terminField.removeEventListener('click', handleFieldClick);
        }
        clearInterval(checkInterval);
      };
    }
    
    // Step 8 (Schritt 6): Warte auf Eingabe im "Zuletzt kontaktiert" Feld
    // Validierung: Datum muss ÜBER 31 Tage her sein (deutsche Zeit)
    if (step.target === '[data-field="zuletzt-kontaktiert"]' && step.requireInput) {
      
      // Hilfsfunktion: Prüfe ob Datum über 31 Tage her ist (deutsche Zeit)
      const isOver31DaysAgo = (dateString) => {
        if (!dateString) return false;
        
        console.log('🔍 Tour Debug Schritt 6 - Input dateString:', dateString);
        
        // Hole heutiges Datum in deutscher Zeitzone
        const now = new Date();
        const germanFormatter = new Intl.DateTimeFormat('de-DE', {
          timeZone: 'Europe/Berlin',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        });
        const todayParts = germanFormatter.formatToParts(now);
        const todayYear = parseInt(todayParts.find(p => p.type === 'year').value, 10);
        const todayMonth = parseInt(todayParts.find(p => p.type === 'month').value, 10);
        const todayDay = parseInt(todayParts.find(p => p.type === 'day').value, 10);
        
        // Parse Input-Datum
        let inputYear, inputMonth, inputDay;
        
        if (dateString.includes('-')) {
          // Format: YYYY-MM-DD oder YYYY-MM-DDTHH:MM
          const datePart = dateString.split('T')[0];
          const parts = datePart.split('-');
          inputYear = parseInt(parts[0], 10);
          inputMonth = parseInt(parts[1], 10);
          inputDay = parseInt(parts[2], 10);
        } else if (dateString.includes('.')) {
          // Format: DD.MM.YYYY
          const parts = dateString.split('.');
          inputDay = parseInt(parts[0], 10);
          inputMonth = parseInt(parts[1], 10);
          inputYear = parseInt(parts[2], 10);
        } else {
          return false;
        }
        
        // Berechne Differenz in Tagen
        const todayDate = new Date(todayYear, todayMonth - 1, todayDay);
        const inputDate = new Date(inputYear, inputMonth - 1, inputDay);
        const diffTime = todayDate.getTime() - inputDate.getTime();
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        
        console.log('🔍 Tour Debug Schritt 6 - Differenz Tage:', diffDays, '(muss > 31 sein)');
        
        return diffDays > 31;
      };
      
      const handleZuletztInput = () => {
        // Input-Element bei jedem Check neu abfragen (falls sich DOM ändert)
        const zuletztInput = document.querySelector('input#letzter_termin');
        if (zuletztInput && zuletztInput.value) {
          // Validiere ob es über 31 Tage her ist
          if (isOver31DaysAgo(zuletztInput.value)) {
            setActionCompleted(true);
            setDateValidationError(false);
          } else {
            setActionCompleted(false);
            setDateValidationError(true);
          }
        } else {
          setActionCompleted(false);
          setDateValidationError(false);
        }
      };
      
      // Event-Listener für Klick auf das Feld (öffnet DatePicker)
      const handleFieldClick = () => {
        setDatePickerOpen(true);
      };
      
      const zuletztField = document.querySelector('[data-field="zuletzt-kontaktiert"]');
      if (zuletztField) {
        zuletztField.addEventListener('click', handleFieldClick);
      }
      
      // Prüfe initial
      handleZuletztInput();
      
      // Periodisch prüfen (alle 300ms für schnellere Reaktion)
      const checkInterval = setInterval(handleZuletztInput, 300);
      
      return () => {
        if (zuletztField) {
          zuletztField.removeEventListener('click', handleFieldClick);
        }
        clearInterval(checkInterval);
      };
    }
    
    // Step 6: Warte auf Klick auf "Ja" bei Kontaktiert
    if (step.target === '[data-field="kontaktiert"]' && step.requireClick) {
      const field = document.querySelector('[data-field="kontaktiert"]');
      if (field) {
        setTimeout(() => scrollToFieldHelper(field, 200), 300);
      }
      
      const checkInterval = setInterval(() => {
        const checkedField = document.querySelector('[data-field="kontaktiert"][data-value="true"]');
        if (checkedField) {
          setActionCompleted(true);
          clearInterval(checkInterval);
        }
      }, 300);
      
      return () => clearInterval(checkInterval);
    }
    
    // Step 5: Warte auf Klick auf den "Nächster Termin" Button (öffnet Termin-Wizard)
    if (step.target === '[data-field="naechster-termin"]' && step.requireClick) {
      const terminDiv = document.querySelector('[data-field="naechster-termin"]');
      if (terminDiv) {
        // Scrolle so, dass der Button direkt ÜBER dem Tour-Modal erscheint (Mobile-kompatibel)
        setTimeout(() => {
          const viewportHeight = window.innerHeight;
          const tourModal = document.querySelector('div.fixed.bg-white.rounded-lg.shadow-2xl');
          const modalHeight = tourModal ? tourModal.offsetHeight : 280;
          const modalTopY = viewportHeight - 20 - modalHeight;
          const targetButtonBottom = modalTopY - 20;

          const bodyLocked = document.body.style.overflow === 'hidden';
          const modalContainer = document.querySelector('.fixed.inset-0 .overflow-y-auto')
                              || document.querySelector('.fixed.inset-0 [class*="overflow-y"]');

          if (bodyLocked && modalContainer) {
            const containerRect = modalContainer.getBoundingClientRect();
            const elementRect = terminDiv.getBoundingClientRect();
            const scrollDelta = elementRect.bottom - targetButtonBottom;
            modalContainer.scrollTo({ top: Math.max(0, modalContainer.scrollTop + scrollDelta), behavior: 'smooth' });
          } else {
            const rect = terminDiv.getBoundingClientRect();
            const scrollDelta = rect.bottom - targetButtonBottom;
            window.scrollTo({ top: Math.max(0, window.scrollY + scrollDelta), behavior: 'smooth' });
          }
        }, 400);

        const handleTerminClick = () => {
          // Wizard öffnet sich – nach kurzer Wartezeit zum nächsten Schritt
          setTimeout(() => {
            setCurrentStep(prev => prev + 1);
          }, 800);
        };

        terminDiv.addEventListener('click', handleTerminClick);
        return () => terminDiv.removeEventListener('click', handleTerminClick);
      }
    }

    // Step 6: Warte auf Datumseingabe im Termin-Wizard (heute)
    if (step.target === '[data-tour="termin-wizard-datum-feld"]' && step.requireInput) {
      const checkDateInput = () => {
        const dateInput = document.querySelector('[data-tour="termin-wizard-datum-feld"] input[type="date"]');
        if (dateInput && dateInput.value) {
          setActionCompleted(true);
        } else {
          setActionCompleted(false);
        }
      };
      checkDateInput();
      const dateCheckInterval = setInterval(checkDateInput, 200);
      return () => clearInterval(dateCheckInterval);
    }

    // Step 7: Uhrzeit – Tour geht weiter wenn Wizard "Weiter →" geklickt wird
    if (step.target === '[data-tour="termin-wizard-uhrzeit-feld"]' && step.requireClick) {
      let weiterBtn = null;
      const handleWizardWeiter = () => {
        setTimeout(() => setCurrentStep(prev => prev + 1), 600);
      };
      const findBtn = setInterval(() => {
        const btn = document.querySelector('[data-tour="termin-wizard-weiter-btn"]');
        if (btn) {
          clearInterval(findBtn);
          weiterBtn = btn;
          btn.addEventListener('click', handleWizardWeiter, { once: true });
        }
      }, 200);
      return () => {
        clearInterval(findBtn);
        if (weiterBtn) weiterBtn.removeEventListener('click', handleWizardWeiter);
      };
    }

    // Wizard Schritt 4 (informational): Zuletzt kontaktiert erklären – kein requireClick, alles gesperrt
    if (step.target === '[data-tour="termin-wizard-zuletzt-kontaktiert"]' && !step.requireClick) {
      setActionCompleted(true);
      const blockClicks = () => {
        const content = document.querySelector('[data-tour="termin-wizard-zuletzt-kontaktiert"]');
        if (content) content.style.pointerEvents = 'none';
      };
      blockClicks();
      const interval = setInterval(blockClicks, 150);
      return () => {
        clearInterval(interval);
        const content = document.querySelector('[data-tour="termin-wizard-zuletzt-kontaktiert"]');
        if (content) content.style.pointerEvents = '';
      };
    }

    // Wizard Schritt 4 (test): Zuletzt kontaktiert – Tour geht weiter wenn Wizard "Weiter →" geklickt wird
    if (step.target === '[data-tour="termin-wizard-zuletzt-kontaktiert"]' && step.requireClick) {
      let weiterBtn = null;
      const handleZuletztWeiter = () => {
        setTimeout(() => setCurrentStep(prev => prev + 1), 600);
      };
      const findBtn = setInterval(() => {
        const btn = document.querySelector('[data-tour="termin-wizard-zuletzt-weiter"]');
        if (btn) {
          clearInterval(findBtn);
          weiterBtn = btn;
          btn.addEventListener('click', handleZuletztWeiter, { once: true });
        }
      }, 200);
      return () => {
        clearInterval(findBtn);
        if (weiterBtn) weiterBtn.removeEventListener('click', handleZuletztWeiter);
      };
    }

    // Wizard Schritt 5: Gesprächsverlauf – Tour geht weiter wenn Wizard "Weiter →" geklickt wird
    if (step.target === '[data-tour="termin-wizard-gespraechsverlauf"]' && step.requireClick) {
      let weiterBtn = null;
      const handleVerlaufWeiter = () => {
        setTimeout(() => setCurrentStep(prev => prev + 1), 600);
      };
      const findBtn = setInterval(() => {
        const btn = document.querySelector('[data-tour="termin-wizard-verlauf-weiter"]');
        if (btn) {
          clearInterval(findBtn);
          weiterBtn = btn;
          btn.addEventListener('click', handleVerlaufWeiter, { once: true });
        }
      }, 200);
      return () => {
        clearInterval(findBtn);
        if (weiterBtn) weiterBtn.removeEventListener('click', handleVerlaufWeiter);
      };
    }

    // Wizard Schritt 6: Nächstes Thema – Tour geht weiter wenn "Fertig ✓" geklickt wird
    if (step.target === '[data-tour="termin-wizard-naechstes-thema"]' && step.requireClick) {
      let fertigBtn = null;
      const handleFertigClick = () => {
        setTimeout(() => setCurrentStep(prev => prev + 1), 800);
      };
      const findBtn = setInterval(() => {
        const btn = document.querySelector('[data-tour="termin-wizard-fertig-btn"]');
        if (btn) {
          clearInterval(findBtn);
          fertigBtn = btn;
          btn.addEventListener('click', handleFertigClick, { once: true });
        }
      }, 200);
      return () => {
        clearInterval(findBtn);
        if (fertigBtn) fertigBtn.removeEventListener('click', handleFertigClick);
      };
    }

    // Step 9: Warte auf Auswahl von "Teampartner" bei Beförderung
    if (step.target === '[data-field="tp-kunde-geworden"]' && step.requireClick) {
      const field = document.querySelector('[data-field="tp-kunde-geworden"]');
      if (field) {
        setTimeout(() => scrollToFieldHelper(field, 200), 300);
      }
      
      // Event-Listener auf alle Radio-Buttons - AUTO-ADVANCE nach Auswahl
      const radioButtons = document.querySelectorAll('input[name="tp_kunde_geworden"]');
      const handleChange = (e) => {
        if (e.target.value === 'teampartner') {
          // Warte kurz, dann AUTOMATISCH zum nächsten Schritt
          setTimeout(() => {
            setCurrentStep(currentStep + 1);
          }, 800);
        }
      };
      
      radioButtons.forEach(radio => {
        radio.addEventListener('change', handleChange);
        radio.addEventListener('click', handleChange);
      });
      
      return () => {
        radioButtons.forEach(radio => {
          radio.removeEventListener('change', handleChange);
          radio.removeEventListener('click', handleChange);
        });
      };
    }
    
    // Step 15: Warte auf Klick auf "Speichern" Button
    if (step.target === '[data-tour="speichern-button"]' && step.requireClick) {
      const button = document.querySelector('[data-tour="speichern-button"]');
      const handleClick = () => {
        // Nach Speichern-Klick zum Modal-Warte-Schritt
        setTimeout(() => {
          setCurrentStep(currentStep + 1);
        }, 800);
      };
      
      if (button) {
        button.addEventListener('click', handleClick);
        return () => button.removeEventListener('click', handleClick);
      }
    }
    
    // Step 15b: Warte auf Beförderungsmodal und dessen Schließung
    if (step.waitForModal) {
      setActionCompleted(false);
      
      // Warte auf das Modal und dessen "Weiter geht's" Button
      let attempts = 0;
      const maxAttempts = 50;
      
      const waitInterval = setInterval(() => {
        // Suche nach dem "Weiter geht's" Button im Modal
        const weiterButton = Array.from(document.querySelectorAll('button')).find(
          btn => btn.textContent.includes("Weiter geht") || btn.textContent.includes("weiter")
        );
        
        attempts++;
        
        if (weiterButton) {
          clearInterval(waitInterval);
          console.log(`✅ Beförderungsmodal gefunden nach ${attempts * 200}ms`);
          
          // Warte auf Klick auf den Button
          const handleModalClose = () => {
            console.log('Modal wird geschlossen...');
            // Warte bis Modal geschlossen ist und gehe zum nächsten Schritt
            setTimeout(() => {
              setCurrentStep(currentStep + 1);
            }, 1000);
          };
          
          weiterButton.addEventListener('click', handleModalClose, { once: true });
          setActionCompleted(true);
        } else if (attempts >= maxAttempts) {
          clearInterval(waitInterval);
          console.warn('⚠️ Beförderungsmodal nicht gefunden, überspringe...');
          setCurrentStep(currentStep + 1);
        }
      }, 200);
      
      return () => clearInterval(waitInterval);
    }
    
    // Step 17: Warte auf Kontakt-Karte (nach Erstellen)
    if (step.waitForCard && !step.requireClick) {
      setActionCompleted(true);
      
      // Warte auf die erste Kontakt-Karte
      let attempts = 0;
      const maxAttempts = 30;
      
      const waitInterval = setInterval(() => {
        const cards = document.querySelectorAll('[data-tour="kontakt-karte"]');
        attempts++;
        
        if (cards.length > 0) {
          clearInterval(waitInterval);
          console.log(`✅ Kontakt-Karte gefunden nach ${attempts * 200}ms`);
          // Scrolle nach oben zur Karten-Ansicht
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else if (attempts >= maxAttempts) {
          clearInterval(waitInterval);
          console.warn('⚠️ Kontakt-Karte nicht gefunden');
        }
      }, 200);
      
      return () => clearInterval(waitInterval);
    }

    // Step 24/30: Warte auf Ausklappen der Kachel (requireClick + waitForCard)
    if (step.waitForCard && step.requireClick) {
      // Warte zuerst auf die Karte, dann auf den Expand-Klick
      const waitForExpandInterval = setInterval(() => {
        const expandBtn = document.querySelector('[data-tour="kachel-expand-button"]');
        if (expandBtn) {
          clearInterval(waitForExpandInterval);
          window.scrollTo({ top: 0, behavior: 'smooth' });

          // Jetzt auf Klick warten (data-expanded wird true)
          const checkExpanded = setInterval(() => {
            const btn = document.querySelector('[data-tour="kachel-expand-button"]');
            if (btn && btn.getAttribute('data-expanded') === 'true') {
              clearInterval(checkExpanded);
              setActionCompleted(true);
            }
          }, 150);

          // Cleanup nach 60s
          setTimeout(() => clearInterval(checkExpanded), 60000);
        }
      }, 200);

      return () => clearInterval(waitForExpandInterval);
    }
    
    // Step 18: Warte auf Klick auf "Schulden" Tab
    if (step.target === '[data-tour="tab-schulden"]' && step.requireClick) {
      const tab = document.querySelector('[data-tour="tab-schulden"]');
      const handleClick = () => {
        // Warte kurz, dann zum nächsten Schritt
        setTimeout(() => {
          setCurrentStep(currentStep + 1);
        }, 800);
      };
      
      if (tab) {
        tab.addEventListener('click', handleClick);
        return () => tab.removeEventListener('click', handleClick);
      }
    }
    
    // Für Schritte ohne erforderliche Aktion - scrolle zum Element
    // Auch bei blockClicks mit scrollToElement Flag
    const shouldScroll = (!step.requireClick && !step.requireInput && step.target !== 'body' && !step.disableManualScroll) 
                        || (step.scrollToElement && step.target !== 'body');
    if (shouldScroll) {
      setActionCompleted(true);
      
      // Funktion zum Scrollen zum Element (Original-Verhalten)
      const scrollToElement = (element) => {
        const rect = element.getBoundingClientRect();
        const modalHeight = 200;
        const extraScroll = step.extraScroll || 0;
        const scrollOffset = step.scrollOffset || 0;
        const targetPosition = window.pageYOffset + rect.top - modalHeight - extraScroll + scrollOffset;
        const scrollBehavior = step.fastScroll ? 'auto' : 'smooth';
        window.scrollTo({ top: Math.max(0, targetPosition), behavior: scrollBehavior });
      };
      
      // Wenn waitForElement aktiviert ist, warte auf das Element
      if (step.waitForElement) {
        const waitForElementInterval = setInterval(() => {
          const element = document.querySelector(step.target);
          if (element) {
            clearInterval(waitForElementInterval);
            setTimeout(() => {
              const freshElement = document.querySelector(step.target);
              if (freshElement) scrollToElement(freshElement);
            }, step.waitDelay || 800);
          }
        }, 100);
        setTimeout(() => clearInterval(waitForElementInterval), 5000);
      } else {
        // Standard: Scrolle nach kurzer Verzögerung
        const element = document.querySelector(step.target);
        if (element) {
          const delay = step.fastScroll ? 100 : step.delayedScroll ? 800 : 300;
          setTimeout(() => {
            scrollToElement(element);
          }, delay);
        }
      }
    } else if (step.target === 'body' || step.disableManualScroll) {
      setActionCompleted(true);
    }
  }, [isActive, currentStep, currentPage]);
  
  // Event-Listener für interaktive Aktionen im Eingangsliste-Bereich
  useEffect(() => {
    if (!isActive || currentPage !== 'eingangsliste') return;
    
    const step = getTourSteps()[currentStep];
    if (!step) return;
    
    // Reset actionCompleted when step changes
    setActionCompleted(false);
    
    // Eingangsliste-Tour: Eingang hinzufügen Button
    if (step.target === '[data-tour="eingang-hinzufuegen-button"]' && step.requireClick) {
      const button = document.querySelector('[data-tour="eingang-hinzufuegen-button"]');
      const handleClick = () => {
        // Nach Klick weiter zum nächsten Schritt
        setTimeout(() => {
          setCurrentStep(currentStep + 1);
        }, 800);
      };
      
      if (button) {
        button.addEventListener('click', handleClick);
        return () => button.removeEventListener('click', handleClick);
      }
    }
    
    // Eingangsliste-Tour: Formular absenden
    if (step.target === '[data-tour="eingang-formular"]' && step.requireFormSubmit) {
      // Warte auf das Formular
      const waitForForm = setInterval(() => {
        const form = document.querySelector('[data-tour="eingang-formular"]');
        if (form) {
          clearInterval(waitForForm);
          
          // Warte auf Speichern-Button Klick oder Form-Submit
          const handleSubmit = () => {
            console.log('📝 Formular abgesendet - gehe zum nächsten Schritt');
            // Warte bis Formular geschlossen ist und Eintrag sichtbar
            setTimeout(() => {
              setCurrentStep(currentStep + 1);
            }, 1500);
          };
          
          // Speichern-Button finden
          const submitButton = form.querySelector('button[type="submit"]');
          if (submitButton) {
            submitButton.addEventListener('click', handleSubmit, { once: true });
          }
          
          // Form submit event
          form.addEventListener('submit', handleSubmit, { once: true });
        }
      }, 200);
      
      return () => clearInterval(waitForForm);
    }
    
    // Eingangsliste-Tour: Warte auf Quartal-Eintrag nach Speichern
    if (step.target === '[data-tour="quartal-eintrag"]' && step.waitForElement) {
      setActionCompleted(true);
      
      let attempts = 0;
      const maxAttempts = 30;
      
      const waitInterval = setInterval(() => {
        const quartalEintrag = document.querySelector('[data-tour="quartal-eintrag"]');
        attempts++;
        
        if (quartalEintrag) {
          clearInterval(waitInterval);
          console.log(`✅ Quartal-Eintrag gefunden nach ${attempts * 200}ms`);
          // Scrolle zum Eintrag
          if (step.scrollToElement) {
            setTimeout(() => {
              quartalEintrag.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 300);
          }
        } else if (attempts >= maxAttempts) {
          clearInterval(waitInterval);
          console.warn('⚠️ Quartal-Eintrag nicht gefunden');
          // Trotzdem weitergehen
          setCurrentStep(currentStep + 1);
        }
      }, 200);
      
      return () => clearInterval(waitInterval);
    }
    
    // Eingangsliste-Tour: Zurück zu Inventory Dashboard
    if (step.requireBackNavigation) {
      const backLink = document.querySelector('[data-tour="zurueck-button"]');
      if (backLink) {
        const handleClick = () => {
          console.log('🔙 Zurück zu Inventory Dashboard');
          // Tour soll auf Inventory-Dashboard bei Bestand weitergehen - localStorage für Konsistenz
          localStorage.setItem('burningTeamTour', JSON.stringify({
            shouldStart: true,
            page: 'inventory-bestand',
            globalStep: 9 // 3 (Inventory) + 6 (Eingangsliste) = 9
          }));
        };
        
        // Beide Events für Desktop und Mobile
        backLink.addEventListener('click', handleClick, { once: true, capture: true });
        backLink.addEventListener('touchend', handleClick, { once: true, capture: true });
        
        return () => {
          backLink.removeEventListener('click', handleClick, { capture: true });
          backLink.removeEventListener('touchend', handleClick, { capture: true });
        };
      }
    }
    
    // Für Schritte ohne Aktion - actionCompleted setzen
    if (!step.requireClick && !step.requireFormSubmit && !step.requireBackNavigation) {
      setActionCompleted(true);
    }
  }, [isActive, currentStep, currentPage]);

  // Event-Listener für Inventory-Bestand-Seite (Dashboard mit Bestand-Klick)
  useEffect(() => {
    if (!isActive || currentPage !== 'inventory-bestand') return;
    
    const step = getTourSteps()[currentStep];
    if (!step) return;
    
    // Reset actionCompleted when step changes
    setActionCompleted(false);
    
    // Inventory-Dashboard: Klick auf Bestand
    if (step.requireClickBestand) {
      const bestandButton = document.querySelector('[data-tour="bestand-button"]');
      if (bestandButton) {
        const handleClick = (e) => {
          console.log('📊 Bestand geklickt');
          localStorage.setItem('burningTeamTour', JSON.stringify({
            shouldStart: true,
            page: 'uebersicht',
            globalStep: 10
          }));
        };
        
        bestandButton.addEventListener('click', handleClick, { once: true, capture: true });
        bestandButton.addEventListener('touchend', handleClick, { once: true, capture: true });
        
        return () => {
          bestandButton.removeEventListener('click', handleClick, { capture: true });
          bestandButton.removeEventListener('touchend', handleClick, { capture: true });
        };
      }
    }
    
    // Für Schritte ohne Bestand-Klick - actionCompleted setzen
    if (!step.requireClickBestand) {
      setActionCompleted(true);
    }
  }, [isActive, currentStep, currentPage]);

  // Event-Listener für Uebersicht-Seite (Bestandsübersicht)
  useEffect(() => {
    if (!isActive || currentPage !== 'uebersicht') return;
    
    const step = getTourSteps()[currentStep];
    if (!step) return;
    
    // Reset actionCompleted when step changes
    setActionCompleted(false);
    
    // Übersicht-Tour: Zurück zum Dashboard für Geschenk-Erklärung
    if (step.requireNavigateToDashboardForGeschenk) {
      const backLink = document.querySelector('[data-tour="zurueck-dashboard-button"]');
      if (backLink) {
        const handleClick = () => {
          console.log('🔙 Zurück zu Dashboard für Geschenk');
          localStorage.setItem('burningTeamTour', JSON.stringify({
            shouldStart: true,
            page: 'geschenk',
            globalStep: 13
          }));
        };
        
        backLink.addEventListener('click', handleClick, { once: true, capture: true });
        backLink.addEventListener('touchend', handleClick, { once: true, capture: true });
        
        return () => {
          backLink.removeEventListener('click', handleClick, { capture: true });
          backLink.removeEventListener('touchend', handleClick, { capture: true });
        };
      }
    }
    
    // Für Schritte ohne Navigation-Aktion - actionCompleted setzen
    if (!step.requireNavigateToDashboardForGeschenk) {
      setActionCompleted(true);
    }
  }, [isActive, currentStep, currentPage]);

  // Event-Listener für Geschenk-Seite (Dashboard mit Geschenk-Tour)
  useEffect(() => {
    if (!isActive || currentPage !== 'geschenk') return;
    
    const step = getTourSteps()[currentStep];
    if (!step) return;
    
    // Reset actionCompleted when step changes
    setActionCompleted(false);
    
    // Für alle Geschenk-Schritte - actionCompleted setzen (keine User-Interaktion nötig)
    setActionCompleted(true);
    
    // Scroll zum Geschenke-Button wenn nötig
    if (step.scrollToElement && step.target !== 'body') {
      const element = document.querySelector(step.target);
      if (element) {
        setTimeout(() => {
          const rect = element.getBoundingClientRect();
          const scrollOffset = step.scrollOffset || 100;
          const targetPosition = window.pageYOffset + rect.top - scrollOffset;
          window.scrollTo({ top: Math.max(0, targetPosition), behavior: 'smooth' });
        }, 300);
      }
    }
  }, [isActive, currentStep, currentPage]);

  // Spotlight-Effekt aktualisieren + Auto-Scroll + Scrollen deaktivieren
  useEffect(() => {
    if (!isActive) return;

    const step = getTourSteps()[currentStep];
    
    // Spezialfall: Sofort nach oben scrollen (ohne Animation)
    if (step && step.scrollToTopInstant) {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
    // Spezialfall: Smooth nach oben scrollen
    else if (step && step.scrollToTop) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    
    if (step && step.target) {
      // Funktion zum Setzen des Spotlight
      const setupSpotlight = (element) => {
        // Bei scrollToTop: Nicht zum Element scrollen, sondern ganz oben bleiben
        if (step.scrollToTop) {
          // Bereits oben gescrollt, nichts tun
        } else if (step.scrollToMainElement) {
          // Scrolle so dass der Button im unteren sichtbaren Bereich bleibt (modal ist unten)
          const rect = element.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          // Ziel: Button im Bereich 50-75% von oben (unter der Mitte, über dem Modal)
          const targetTop = window.pageYOffset + rect.top - (viewportHeight * 0.55);
          window.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
        } else if (step.scrollToTargetWithModalOffset) {
          // Scrolle so dass das Element unter dem Modal sichtbar ist
          // Modal ist bei top: 20px + ~200px Höhe = 220px, dann noch 80px extra Abstand
          const rect = element.getBoundingClientRect();
          const modalBottomOffset = 350; // Modal + extra Padding damit Button gut sichtbar ist
          const targetPosition = window.pageYOffset + rect.top - modalBottomOffset;
          window.scrollTo({ top: Math.max(0, targetPosition), behavior: 'smooth' });
        } else if (step.waitForElement) {
          // Für waitForElement: Manuelle Scroll-Berechnung mit Modal-Berücksichtigung
          const rect = element.getBoundingClientRect();
          const modalHeight = 280; // Höhe des Tour-Modals + Padding
          const targetPosition = window.pageYOffset + rect.top - modalHeight;
          window.scrollTo({ top: Math.max(0, targetPosition), behavior: 'smooth' });
        } else {
          // Standard-Verhalten für andere Schritte
          element.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'nearest',
            inline: 'center'
          });
          
          // Manuelle Offsets für spezifische Buttons
          setTimeout(() => {
            if (step.target === '[data-tour="kunden-button"]') {
              // Kunde: Mehr nach oben scrollen
              window.scrollBy({ top: -80, behavior: 'smooth' });
            } else if (step.target === '[data-tour="inventory-button"]') {
              // Inventory: Eine Button-Höhe nach unten
              const buttonHeight = element.getBoundingClientRect().height;
              window.scrollBy({ top: buttonHeight + 10, behavior: 'smooth' });
            } else if (step.placement === 'top-fixed') {
              // Für top-fixed: Scrolle so dass Element unter dem Modal sichtbar ist
              const rect = element.getBoundingClientRect();
              const modalHeight = 280; // Modal Höhe + top offset (80px) + padding
              const targetTop = rect.top + window.pageYOffset - modalHeight;
              window.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
            }
          }, 100);
        }
        
        // Berechne Spotlight-Delay basierend auf step-Eigenschaften
        // Für waitForElement: Längere Wartezeit für Layout-Stabilisierung
        const spotlightDelay = step.waitForElement ? 1500 : 700;
        
        // Nach dem Scrollen das Element setzen und dann Scrollen blockieren
        setTimeout(() => {
          // Element erneut abfragen für aktuelle Position
          const targetElement = document.querySelector(step.target);
          if (targetElement) {
            setSpotlightTarget(targetElement);
            setSpotlightKey(prev => prev + 1); // Force re-render
            
            // Scrollen während Tour deaktivieren (außer wenn allowUserScroll aktiviert ist)
            if (!step.allowUserScroll) {
              const scrollY = window.scrollY;
              document.body.style.overflow = 'hidden';
              document.body.style.height = '100vh';
              document.body.style.touchAction = 'none'; // Für Mobile
              window.scrollTo(0, scrollY); // Position beibehalten
            } else {
              // Scroll erlauben - Styles zurücksetzen falls vorher blockiert
              document.body.style.overflow = '';
              document.body.style.height = '';
              document.body.style.touchAction = '';
              
              // Spotlight bei Scroll aktualisieren
              const handleScroll = () => {
                setSpotlightKey(prev => prev + 1); // Force re-render für neue Position
              };
              window.addEventListener('scroll', handleScroll);
              // Cleanup wird vom useEffect return übernommen
            }
            
            // Zusätzliche Neuberechnung nach weiteren 300ms für waitForElement
            if (step.waitForElement) {
              setTimeout(() => {
                const finalElement = document.querySelector(step.target);
                if (finalElement) {
                  setSpotlightTarget(finalElement);
                  setSpotlightKey(prev => prev + 1);
                }
              }, 300);
            }
            
            // Scroll zum Target-Element wenn scrollToTarget gesetzt ist
            if (step.scrollToTarget && targetElement) {
              setTimeout(() => {
                targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }, 100);
            }
            
            // Auto-Scroll für Tabellen auf Mobile
            if (step.autoScrollTable && targetElement) {
              const isMobile = window.innerWidth < 768;
              if (isMobile) {
                // Langsames Auto-Scroll nach rechts und zurück
                let scrollPos = 0;
                const scrollContainer = targetElement;
                const maxScroll = scrollContainer.scrollWidth - scrollContainer.clientWidth;
                
                if (maxScroll > 0) {
                  const scrollInterval = setInterval(() => {
                    scrollPos += 2; // Langsam scrollen
                    if (scrollPos <= maxScroll) {
                      scrollContainer.scrollLeft = scrollPos;
                    } else {
                      // Am Ende angekommen - stoppen
                      clearInterval(scrollInterval);
                      // Nach 1 Sekunde zurück scrollen
                      setTimeout(() => {
                        scrollContainer.scrollTo({ left: 0, behavior: 'smooth' });
                      }, 1000);
                    }
                  }, 30); // 30ms für smooth animation
                  
                  // Cleanup nach 10 Sekunden falls noch läuft
                  setTimeout(() => clearInterval(scrollInterval), 10000);
                }
              }
            }
            
            // Demo-Spalten highlighten
            if (step.highlightDemoColumns) {
              const demoSpalte = document.querySelector('[data-tour="demo-spalte"]');
              const demoProzentSpalte = document.querySelector('[data-tour="demo-prozent-spalte"]');
              
              // Header highlighten
              if (demoSpalte) {
                demoSpalte.style.backgroundColor = '#fef3c7';
                demoSpalte.style.boxShadow = '0 0 0 3px #fbbf24';
              }
              if (demoProzentSpalte) {
                demoProzentSpalte.style.backgroundColor = '#fef3c7';
                demoProzentSpalte.style.boxShadow = '0 0 0 3px #fbbf24';
              }
              
              // Erste Zeile mit Beispielwerten versehen
              const tableRows = document.querySelectorAll('[data-tour="bestand-tabelle"] tbody tr');
              if (tableRows.length > 0) {
                const firstRow = tableRows[0];
                const cells = firstRow.querySelectorAll('td');
                // Demos ist Spalte 8 (0-indexed: 7), Demo % ist Spalte 10 (0-indexed: 9)
                if (cells.length >= 10) {
                  const demoCell = cells[7]; // Demos Spalte
                  const demoProzentCell = cells[9]; // Demo % Spalte
                  
                  // Originale Werte speichern
                  window.tourOriginalDemoValue = demoCell.innerHTML;
                  window.tourOriginalDemoProzentValue = demoProzentCell.innerHTML;
                  
                  // Beispielwerte setzen mit Highlight
                  demoCell.innerHTML = '<span style="background:#fef3c7;padding:2px 6px;border-radius:4px;font-weight:bold;color:#d97706;">1</span>';
                  demoProzentCell.innerHTML = '<span style="display:inline-block;padding:2px 8px;border-radius:4px;background:#c7d2fe;color:#4338ca;font-size:12px;font-weight:600;">75%</span>';
                  
                  // Zellen highlighten
                  demoCell.style.backgroundColor = '#fef3c7';
                  demoProzentCell.style.backgroundColor = '#fef3c7';
                }
              }
              
              setHighlightedColumns([demoSpalte, demoProzentSpalte].filter(Boolean));
              
              // Auf Mobile: Zur Demo-Spalte scrollen
              const isMobile = window.innerWidth < 768;
              if (isMobile && demoSpalte && targetElement) {
                const scrollContainer = targetElement;
                const columnRect = demoSpalte.getBoundingClientRect();
                const containerRect = scrollContainer.getBoundingClientRect();
                const scrollTarget = scrollContainer.scrollLeft + (columnRect.left - containerRect.left) - 50;
                scrollContainer.scrollTo({ left: scrollTarget, behavior: 'smooth' });
              }
            } else {
              // Highlight entfernen
              highlightedColumns.forEach(col => {
                if (col) {
                  col.style.backgroundColor = '';
                  col.style.boxShadow = '';
                }
              });
              setHighlightedColumns([]);
              
              // Originale Werte wiederherstellen
              const tableRows = document.querySelectorAll('[data-tour="bestand-tabelle"] tbody tr');
              if (tableRows.length > 0 && window.tourOriginalDemoValue !== undefined) {
                const firstRow = tableRows[0];
                const cells = firstRow.querySelectorAll('td');
                if (cells.length >= 10) {
                  cells[7].innerHTML = window.tourOriginalDemoValue;
                  cells[9].innerHTML = window.tourOriginalDemoProzentValue;
                  cells[7].style.backgroundColor = '';
                  cells[9].style.backgroundColor = '';
                }
                delete window.tourOriginalDemoValue;
                delete window.tourOriginalDemoProzentValue;
              }
            }
          }
        }, spotlightDelay);
      };
      
      // Wenn waitForElement oder waitForCard aktiviert ist, warte auf das Element
      if (step.waitForElement || step.waitForCard) {
        let attempts = 0;
        const maxAttempts = 50; // 5 Sekunden (50 * 100ms)
        
        const waitInterval = setInterval(() => {
          // Für waitForCard: Nehme immer die ERSTE Karte
          const element = step.waitForCard 
            ? document.querySelectorAll(step.target)[0]
            : document.querySelector(step.target);
          attempts++;
          
          if (element) {
            clearInterval(waitInterval);
            console.log(`✅ Element gefunden nach ${attempts * 100}ms`);
            
            // Für waitForCard: Zuerst nach oben scrollen
            if (step.waitForCard && step.forceScrollTop) {
              window.scrollTo({ top: 0, behavior: 'smooth' });
              // Dann Spotlight nach Scroll setzen
              setTimeout(() => {
                setupSpotlight(element);
              }, 600);
            } else {
              setupSpotlight(element);
            }
          } else if (attempts >= maxAttempts) {
            clearInterval(waitInterval);
            console.warn('⚠️ Element nicht gefunden nach 5 Sekunden');
          }
        }, 100);
      } else {
        // Standard-Verhalten
        const element = document.querySelector(step.target);
        if (element) {
          setupSpotlight(element);
        }
      }
    }

    return () => {
      // Scrollen wieder aktivieren
      document.body.style.overflow = '';
      document.body.style.height = '';
      document.body.style.touchAction = '';
    };
  }, [isActive, currentStep, currentPage]);

  const handleNext = () => {
    const steps = getTourSteps();
    const currentStepData = steps[currentStep];
    
    // Spezialfall: Letzter Schritt mit showWeiterButton
    if (currentStepData && currentStepData.showWeiterButton) {
      setIsActive(false);
      // Scroll-Blocking NICHT setzen – Seite bleibt normal scrollbar
      // Box-Shadow wird via useEffect auf den Karten gesetzt
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setShowHeaderHint(true);
      return;
    }
    
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Ende der aktuellen Seite
      if (currentPage === 'main') {
        // Hauptseite fertig
        setIsActive(false);
        fireTourEnd();
        markTourCompleted();
      } else if (currentPage === 'kunden' || currentPage === 'inventory') {
        // Unterseite fertig
        setIsActive(false);
        fireTourEnd();
        markTourCompleted();
      }
    }
  };

  // Zentrales Tour-Ende: globale Variable + Event für PWAInstallBanner u.a.
  // MUSS vor handleSkipTour und handleSkip stehen (kein Hoisting bei const)
  const fireTourEnd = () => {
    window.burningTeamTourActive = false;
    window.dispatchEvent(new CustomEvent('bt-tour-end'));
  };

  const handleSkipTour = () => {
    // Tour später fortsetzen - zeige Hinweis auf Info-Button
    setShowHeaderHint(false);
    setIsActive(false);
    fireTourEnd();
    setShowSkipHint(true); // Zeige Abbruch-Modal
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const closeSkipHint = async () => {
    setShowSkipHint(false);
    
    // Scroll-Blocking entfernen mit setTimeout um sicherzustellen dass es nach React-Render passiert
    setTimeout(() => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      document.body.removeAttribute('style');
    }, 0);
    
    if (onComplete) {
      onComplete();
    }
    if (!user?.tour_completed) {
      await markTourCompleted();
    }
  };

  const handleSkip = async () => {
    setIsActive(false);
    setShowWelcome(false);
    setShowChoice(false);
    setShowHeaderHint(false);
    // Scroll-Blocking entfernen
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.width = '';
    document.body.style.top = '';
    setShowSkipHint(true); // Zeige Abbruch-Modal
    fireTourEnd();
  };

  const startTour = () => {
    setShowWelcome(false);
    
    // Ganz nach oben scrollen bevor Tour startet (auf allen Seiten!)
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Nach dem Scroll Tour starten
    setTimeout(() => {
      setIsActive(true);
      setCurrentStep(0);
      window.burningTeamTourActive = true;
      window.dispatchEvent(new CustomEvent('bt-tour-start'));
    }, 300);
  };

  const selectNextPage = (page) => {
    setShowChoice(false);
    
    // Setze localStorage für Tour-Fortsetzung
    localStorage.setItem('burningTeamTour', JSON.stringify({
      shouldStart: true,
      page: page
    }));
    
    // Navigation zur gewählten Seite
    if (page === 'kunden') {
      navigate('/kunden');
    } else if (page === 'inventory') {
      navigate('/dashboard');
    }
  };

  // Zentrales Tour-Ende: globale Variable + Event für PWAInstallBanner u.a.
  const markTourCompleted = async () => {
    try {
      await axios.post(`${API}/api/auth/mark-tour-completed`);
      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      console.error('Error marking tour as completed:', error);
    }
  };

  const currentStepData = getTourSteps()[currentStep];
  const totalSteps = getTourSteps().length;
  
  // Berechne globale Schritt-Nummer für Inventory-Bereich (kontinuierliche Nummerierung)
  const getInventoryGlobalStepInfo = () => {
    // Gesamtzahl aller Inventory-Schritte: 
    // inventorySteps (3) + eingangslisteSteps (6) + inventoryBestandSteps (1) + uebersichtSteps (3) + geschenkSteps (7) = 20
    const inventoryTotalSteps = 3 + 6 + 1 + 3 + 7; // Gesamt für alle Inventory-Seiten
    
    const isInventoryArea = ['inventory', 'eingangsliste', 'inventory-bestand', 'uebersicht', 'geschenk'].includes(currentPage);
    
    if (!isInventoryArea) {
      // Für Main-Page: Banner zählt als Schritt 6, also totalSteps + 1
      const mainPageTotal = currentPage === 'main' ? totalSteps + 1 : totalSteps;
      return { displayStep: currentStep + 1, displayTotal: mainPageTotal };
    }
    
    let globalCurrentStep = currentStep + 1;
    
    if (currentPage === 'eingangsliste') {
      // Auf Eingangsliste: +3 (für die 3 Inventory-Schritte davor)
      globalCurrentStep = 3 + currentStep + 1;
    } else if (currentPage === 'inventory-bestand') {
      // Auf Dashboard nach Eingangsliste: +3 (Inventory) +6 (Eingangsliste)
      globalCurrentStep = 3 + 6 + currentStep + 1;
    } else if (currentPage === 'uebersicht') {
      // Auf Übersicht nach Bestand-Klick: +3 (Inventory) +6 (Eingangsliste) +1 (Bestand)
      globalCurrentStep = 3 + 6 + 1 + currentStep + 1;
    } else if (currentPage === 'geschenk') {
      // Auf Dashboard für Geschenk: +3 (Inventory) +6 (Eingangsliste) +1 (Bestand) +3 (Übersicht)
      globalCurrentStep = 3 + 6 + 1 + 3 + currentStep + 1;
    }
    
    return { 
      displayStep: globalCurrentStep, 
      displayTotal: inventoryTotalSteps 
    };
  };
  
  const { displayStep, displayTotal } = getInventoryGlobalStepInfo();

  // Spotlight-Position berechnen
  const getSpotlightStyle = () => {
    if (!spotlightTarget) return {};

    const rect = spotlightTarget.getBoundingClientRect();
    const padding = 10;

    return {
      top: rect.top - padding,
      left: rect.left - padding,
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
    };
  };

  return (
    <>
      {/* CSS Animationen für wackelnden Button und pulsierenden Text */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
          20%, 40%, 60%, 80% { transform: translateX(4px); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
        .tour-date-hint {
          transition: color 0.3s ease;
        }
      `}</style>
      
      {/* Welcome Modal */}
      {showWelcome && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 animate-fade-in">
            <div className="text-center mb-6">
              <div className="bg-gradient-to-r from-emerald-100 to-teal-100 rounded-full h-20 w-20 flex items-center justify-center mx-auto mb-4">
                <span className="text-4xl">{currentPage === 'kunden' ? '👥' : currentPage === 'inventory' ? '📦' : '🎓'}</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-800">
                {currentPage === 'kunden' ? 'Kunden-Bereich Tour' : currentPage === 'inventory' ? 'Inventory-Bereich Tour' : 'Willkommen bei BurningTeam!'}
              </h2>
            </div>

            <p className="text-gray-600 mb-6 text-center">
              {currentPage === 'kunden' ? (
                <>Möchten Sie eine <strong>interaktive Einführung</strong> zum Erstellen von Kontakten?</>
              ) : currentPage === 'inventory' ? (
                <>Möchten Sie eine <strong>interaktive Einführung</strong> zur Warenverwaltung?</>
              ) : (
                <>Möchten Sie eine kurze <strong>interaktive Einführung</strong> in die App?</>
              )}
            </p>

            <div className="space-y-3">
              <button
                onClick={startTour}
                className="w-full px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg font-semibold hover:opacity-90 transition-all shadow-lg"
              >
                ✅ Ja, Tour starten!
              </button>
              <button
                onClick={handleSkip}
                className="w-full px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all"
              >
                Überspringen
              </button>
            </div>

            <p className="text-xs text-gray-500 text-center mt-4">
              💡 Sie können die Tour jederzeit über das ℹ️ Symbol neu starten
            </p>
          </div>
        </div>
      )}

      {/* Choice Modal - Kunden oder Inventory */}
      {showChoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 animate-fade-in">
            <div className="text-center mb-6">
              <div className="bg-gradient-to-r from-blue-100 to-emerald-100 rounded-full h-20 w-20 flex items-center justify-center mx-auto mb-4">
                <span className="text-4xl">🎯</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-800">Was möchten Sie zuerst kennenlernen?</h2>
            </div>

            <p className="text-gray-600 mb-6 text-center">
              Wählen Sie einen Bereich für eine <strong>detaillierte, interaktive Tour</strong>:
            </p>

            <div className="space-y-3">
              <button
                onClick={() => selectNextPage('kunden')}
                className="w-full px-6 py-4 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg font-semibold hover:opacity-90 transition-all shadow-lg text-left"
              >
                <div className="flex items-center gap-3">
                  <span className="text-3xl">👥</span>
                  <div>
                    <div className="font-bold text-lg">Kunden-Bereich</div>
                    <div className="text-sm text-emerald-100">Kontakte verwalten & Kundenbindung</div>
                  </div>
                </div>
              </button>
              
              <button
                onClick={() => selectNextPage('inventory')}
                className="w-full px-6 py-4 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg font-semibold hover:opacity-90 transition-all shadow-lg text-left"
              >
                <div className="flex items-center gap-3">
                  <span className="text-3xl">📦</span>
                  <div>
                    <div className="font-bold text-lg">Inventory-Bereich</div>
                    <div className="text-sm text-blue-100">Warenverwaltung & Rechnungen</div>
                  </div>
                </div>
              </button>

              <button
                onClick={handleSkip}
                className="w-full px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all"
              >
                Tour später fortsetzen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Active Tour Overlay */}
      {isActive && currentStepData && (
        <>
          {/* Dark Overlay mit rechteckigem Spotlight-Cutout */}
          {/* Multi-Spotlight nur wenn explizit definiert */}
          {currentStepData.multiSpotlight && currentStepData.multiSpotlight.length > 0 ? (
            // Multi-Spotlight: Mehrere Elemente hervorheben
            <>
              {currentStepData.multiSpotlight.map((selector, idx) => {
                const element = document.querySelector(selector);
                if (!element) return null;
                const rect = element.getBoundingClientRect();
                
                return (
                  <div key={`multi-spotlight-${idx}`}>
                    {/* Dunkle Bereiche um jedes Element */}
                    {idx === 0 && (
                      <>
                        {/* Oben - bis zum ersten Element */}
                        <div className="fixed left-0 right-0 z-[9998] bg-black bg-opacity-75 pointer-events-auto transition-all duration-100"
                          style={{ top: 0, height: rect.top }} />
                      </>
                    )}
                    
                    {/* Links vom Element */}
                    <div className="fixed z-[9998] bg-black bg-opacity-75 pointer-events-auto transition-all duration-100"
                      style={{ top: rect.top, left: 0, width: rect.left, height: rect.height }} />
                    
                    {/* Rechts vom Element */}
                    <div className="fixed z-[9998] bg-black bg-opacity-75 pointer-events-auto transition-all duration-100"
                      style={{ top: rect.top, left: rect.right, right: 0, height: rect.height }} />
                    
                    {/* Unten - nach letztem Element */}
                    {idx === currentStepData.multiSpotlight.length - 1 && (
                      <div className="fixed left-0 right-0 z-[9998] bg-black bg-opacity-75 pointer-events-auto transition-all duration-100"
                        style={{ top: rect.bottom, bottom: 0 }} />
                    )}
                    
                    {/* Zwischen den Elementen */}
                    {idx < currentStepData.multiSpotlight.length - 1 && (() => {
                      const nextElement = document.querySelector(currentStepData.multiSpotlight[idx + 1]);
                      if (nextElement) {
                        const nextRect = nextElement.getBoundingClientRect();
                        return (
                          <div className="fixed left-0 right-0 z-[9998] bg-black bg-opacity-75 pointer-events-auto transition-all duration-100"
                            style={{ top: rect.bottom, height: nextRect.top - rect.bottom }} />
                        );
                      }
                      return null;
                    })()}
                  </div>
                );
              })}
            </>
          ) : currentStepData.spotlight && spotlightTarget ? (
            <>
              {/* 4 dunkle Bereiche um das Element herum - BLOCKIEREN Klicks */}
              {(() => {
                const rect = spotlightTarget.getBoundingClientRect();
                // Dynamisches Padding: Wenn DatePicker offen ist und expandOnDatePickerOpen gesetzt, verwende erweitertes Padding
                const padding = (currentStepData.expandOnDatePickerOpen && datePickerOpen) 
                  ? (currentStepData.spotlightPaddingExpanded || 250)
                  : (currentStepData.spotlightPadding || 0);
                const top = Math.max(0, rect.top - padding);
                const bottom = rect.bottom + padding;
                const left = Math.max(0, rect.left - padding);
                const right = rect.right + padding;
                const height = rect.height + padding * 2;
                const width = rect.width + padding * 2;
                
                return (
                  <>
                    {/* Oben */}
                    <div
                      key={`spotlight-top-${spotlightKey}`}
                      className="fixed left-0 right-0 z-[9998] bg-black bg-opacity-75 pointer-events-auto transition-all duration-100"
                      style={{
                        top: 0,
                        height: top,
                      }}
                    />
                    
                    {/* Unten */}
                    <div
                      className="fixed left-0 right-0 z-[9998] bg-black bg-opacity-75 pointer-events-auto transition-all duration-100"
                      style={{
                        top: bottom,
                        bottom: 0,
                      }}
                    />
                    
                    {/* Links */}
                    <div
                      className="fixed z-[9998] bg-black bg-opacity-75 pointer-events-auto transition-all duration-100"
                      style={{
                        top: top,
                        left: 0,
                        width: left,
                        height: height,
                      }}
                    />
                    
                    {/* Rechts */}
                    <div
                      className="fixed z-[9998] bg-black bg-opacity-75 pointer-events-auto transition-all duration-100"
                      style={{
                        top: top,
                        left: right,
                        right: 0,
                        height: height,
                      }}
                    />
                    
                    {/* Transparente Spotlight-Zone - blockiert Klicks wenn blockClicks aktiviert */}
                    <div
                      className={`fixed z-[9997] transition-all duration-100 ${
                        currentStepData.blockClicks ? 'pointer-events-auto' : 'pointer-events-none'
                      }`}
                      style={{
                        top: top,
                        left: left,
                        width: width,
                        height: height,
                      }}
                    />
                  </>
                );
              })()}
            </>
          ) : currentStepData.showBackground ? (
            // Kein Overlay - Hintergrund komplett sichtbar
            null
          ) : (
            <div className="fixed inset-0 z-[9998] bg-black bg-opacity-50 pointer-events-none" />
          )}

            {/* Tooltip - Position abhängig von placement */}
            <div
              className={`fixed bg-white rounded-lg shadow-2xl z-[10000] pointer-events-auto ${
                currentStepData.compactModal
                  ? 'py-1.5 px-3 w-[85%] max-w-xs'
                  : currentStepData.smallModal || currentStepData.placement === 'top-small' || currentStepData.placement === 'top-start'
                  ? 'p-3 w-[85%] max-w-xs' 
                  : 'p-4 w-[90%] max-w-md'
              }`}
              style={
                currentStepData.placement === 'center'
                  ? {
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                    }
                  : currentStepData.placement === 'top-fixed'
                  ? {
                      top: '20px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                    }
                  : currentStepData.placement === 'top-small'
                  ? {
                      top: '60px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                    }
                  : currentStepData.placement === 'top-start'
                  ? {
                      top: '10px',
                      left: '12px',
                      right: 'auto',
                      transform: 'none',
                    }
                  : currentStepData.placement === 'top'
                  ? {
                      top: '10px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                    }
                  : {
                      bottom: '20px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                    }
              }
            >
              {/* Header - kompakt für compactModal */}
              <div className={`flex items-start justify-between ${currentStepData.compactModal ? 'mb-1' : 'mb-3'}`}>
                <h3 className={`font-bold text-gray-800 flex-1 pr-3 ${currentStepData.compactModal ? 'text-sm' : 'text-lg'}`}>
                  {currentStepData.title}
                </h3>
                <button
                  onClick={handleSkip}
                  className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                >
                  <svg className={`${currentStepData.compactModal ? 'w-3 h-3' : 'w-4 h-4'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Content - kompakt für compactModal */}
              {/* Bei Validierungsfehler wird der Hinweistext rot gefärbt */}
              <div
                className={`text-gray-600 leading-snug ${currentStepData.compactModal ? 'text-xs mb-1.5' : 'text-sm mb-4'}`}
                dangerouslySetInnerHTML={{ 
                  __html: dateValidationError && (currentStepData.validateTodayDate || currentStepData.validateOldDate)
                    ? currentStepData.content.replace(
                        'class="tour-date-hint"',
                        'class="tour-date-hint" style="color: #dc2626; animation: pulse 1s infinite;"'
                      )
                    : currentStepData.content 
                }}
              />

              {/* Progress - kompakt für compactModal */}
              <div className={`flex items-center gap-2 ${currentStepData.compactModal ? 'mb-1.5' : 'mb-3'}`}>
                <div className={`flex-1 bg-gray-200 rounded-full ${currentStepData.compactModal ? 'h-1' : 'h-1.5'}`}>
                  <div
                    className="bg-emerald-500 h-1.5 rounded-full transition-all duration-300"
                    style={{ width: `${(displayStep / displayTotal) * 100}%` }}
                  />
                </div>
                <span className={`text-gray-500 font-medium ${currentStepData.compactModal ? 'text-[10px]' : 'text-xs'}`}>
                  {displayStep}/{displayTotal}
                </span>
              </div>

              {/* Navigation - kompakt für compactModal */}
              <div className={`flex ${currentStepData.compactModal ? 'gap-1' : 'gap-2'}`}>
                {/* Spezialfall: Inventory Choice */}
                {currentStepData.showKundenFinalChoice ? (
                  <div className="flex flex-col gap-2 w-full">
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          localStorage.setItem('burningTeamTour', JSON.stringify({
                            shouldStart: true,
                            page: 'inventory'
                          }));
                          navigate('/dashboard');
                        }}
                        className="flex-1 px-3 py-2 text-xs bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg font-semibold hover:opacity-90 transition-all shadow-md"
                      >
                        📦 Inventory-Tour →
                      </button>
                      <button
                        onClick={() => {
                          localStorage.setItem('burningTeamTour', JSON.stringify({
                            shouldStart: true,
                            page: 'zielplanung'
                          }));
                          navigate('/zielplanung');
                        }}
                        className="flex-1 px-3 py-2 text-xs bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg font-semibold hover:opacity-90 transition-all shadow-md"
                      >
                        📅 Zielplanungskalender Tour starten
                      </button>
                    </div>
                    <button
                      onClick={handleSkip}
                      className="w-full px-4 py-1.5 text-sm border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all"
                    >
                      Tour beenden
                    </button>
                  </div>
                ) : currentStepData.showInventoryChoice ? (
                  <>
                    <button
                      onClick={handleSkip}
                      className="flex-1 px-4 py-2 text-sm border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all"
                    >
                      Tour beenden
                    </button>
                    <button
                      onClick={() => {
                        // Navigiere zu Inventory und starte Tour
                        localStorage.setItem('burningTeamTour', JSON.stringify({
                          shouldStart: true,
                          page: 'inventory'
                        }));
                        navigate('/dashboard');
                      }}
                      className="flex-1 px-4 py-2 text-sm bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg font-semibold hover:opacity-90 transition-all shadow-lg"
                    >
                      📦 Inventory-Tour →
                    </button>
                  </>
                ) : currentStepData.showKundenTourChoice ? (
                  <>
                    <button
                      onClick={handleSkip}
                      className="flex-1 px-4 py-2 text-sm border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all"
                    >
                      Tour beenden
                    </button>
                    <button
                      onClick={() => {
                        localStorage.setItem('burningTeamTour', JSON.stringify({
                          shouldStart: true,
                          page: 'kunden'
                        }));
                        navigate('/kunden');
                      }}
                      className="flex-1 px-3 py-2 text-xs bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg font-semibold hover:opacity-90 transition-all shadow-lg"
                    >
                      👥 Kunden-Tour →
                    </button>
                    <button
                      onClick={() => {
                        localStorage.setItem('burningTeamTour', JSON.stringify({
                          shouldStart: true,
                          page: 'zielplanung'
                        }));
                        navigate('/zielplanung');
                      }}
                      className="flex-1 px-3 py-2 text-xs bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg font-semibold hover:opacity-90 transition-all shadow-md"
                    >
                      📅 Zielplanung →
                    </button>
                  </>
                ) : currentStepData.noWeiterButton ? (
                  // Kein Button - warte auf externes Event (z.B. Modal-Schließung)
                  <div className="flex-1 px-4 py-1.5 text-sm text-center text-gray-500 italic">
                    Warten auf Benutzeraktion...
                  </div>
                ) : (
                  <>
                    {currentStep > 0 && !currentStepData.hideBackButton && (
                      <button
                        onClick={handleBack}
                        className={`text-gray-600 hover:bg-gray-100 rounded-lg transition-colors font-medium ${currentStepData.compactModal ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'}`}
                      >
                        ← Zurück
                      </button>
                    )}
                    {/* Weiter Button nur anzeigen wenn keine Aktion erforderlich ist ODER Aktion abgeschlossen ist */}
                    {(currentStep < totalSteps - 1 || currentStepData.showWeiterButton) && (
                      (!currentStepData.requireClick && !currentStepData.requireInput && !currentStepData.requireFormSubmit && !currentStepData.requireBackNavigation && !currentStepData.requireClickBestand) || actionCompleted
                    ) ? (
                      <button
                        onClick={handleNext}
                        className={`flex-1 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg font-semibold hover:opacity-90 transition-all shadow-lg ${currentStepData.compactModal ? 'px-2 py-1 text-xs' : 'px-4 py-1.5 text-sm'}`}
                      >
                        Weiter →
                      </button>
                    ) : (currentStepData.requireClick || currentStepData.requireInput || currentStepData.requireFormSubmit || currentStepData.requireBackNavigation || currentStepData.requireClickBestand) && !actionCompleted ? (
                      // Wackelnder Button bei Validierungsfehler (falsches Datum eingegeben)
                      dateValidationError && (currentStepData.validateTodayDate || currentStepData.validateOldDate) ? (
                        <button
                          onClick={() => {
                            // Button wackeln lassen aber nichts tun
                            const btn = document.querySelector('.tour-shake-btn');
                            if (btn) {
                              btn.classList.remove('animate-shake');
                              void btn.offsetWidth; // Trigger reflow
                              btn.classList.add('animate-shake');
                            }
                          }}
                          className="tour-shake-btn flex-1 bg-gradient-to-r from-gray-400 to-gray-500 text-white rounded-lg font-semibold transition-all shadow-lg animate-shake px-4 py-1.5 text-sm"
                          style={{
                            animation: 'shake 0.5s ease-in-out'
                          }}
                        >
                          Weiter →
                        </button>
                      ) : (
                        <div className={`flex-1 text-center text-gray-500 italic ${currentStepData.compactModal ? 'px-2 py-1 text-xs' : 'px-4 py-1.5 text-sm'}`}>
                          {currentStepData.requireFormSubmit ? 'Formular ausfüllen...' : 
                           currentStepData.requireBackNavigation ? 'Zurück klicken...' :
                           currentStepData.requireClickBestand ? 'Auf Bestand klicken...' :
                           'Aktion ausführen...'}
                        </div>
                      )
                    ) : null}
                  </>
                )}
              </div>
            </div>
        </>
      )}

      {/* Header Hint Modal - After clicking "Weiter" on final step */}
      {showHeaderHint && (
        <>
          {/* Small banner at top of header - moved higher */}
          <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[10001] w-[90%] max-w-sm animate-fade-in">
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg shadow-2xl p-3">
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-semibold text-sm">🎯 Was möchten Sie zuerst erkunden?</p>
                    <span className="text-xs text-emerald-100 bg-emerald-700/50 px-2 py-0.5 rounded">7/7</span>
                  </div>
                  <p className="text-xs text-emerald-50">
                    Klicken Sie auf <strong>&quot;Kunden&quot;</strong>, <strong>&quot;Zielplanung&quot;</strong> oder <strong>&quot;Inventar&quot;</strong>
                  </p>
                </div>
                <div className="flex flex-col gap-1.5">
                  <button
                    onClick={() => {
                      setShowHeaderHint(false);
                      setIsActive(true);
                      // Zurück zu Schritt 6 (dem letzten Schritt vor dem Banner)
                      setCurrentStep(5); // Index 5 = Schritt 6/7
                    }}
                    className="px-2.5 py-1 bg-white bg-opacity-20 hover:bg-opacity-30 rounded text-xs font-medium transition-all whitespace-nowrap"
                  >
                    ← Zurück
                  </button>
                  <button
                    onClick={handleSkipTour}
                    className="px-2.5 py-1 bg-white bg-opacity-20 hover:bg-opacity-30 rounded text-xs font-medium transition-all whitespace-nowrap"
                  >
                    Tour abbrechen
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          {/* Multi-spotlight für letzten Schritt: Kein fixer Rahmen mehr.
              Box-Shadow wird direkt per JS auf die Karten-Elemente gesetzt (scrollt mit). */}
        </>
      )}
      
      {/* Abbruch-Modal mit Hinweis auf Info-Button */}
      {showSkipHint && (
        <>
          {/* Spotlight auf Info-Button - gleicher Stil wie bei anderen Schritten */}
          {(() => {
            // Finde den sichtbaren Info-Button
            const selectors = [
              '[data-tour="info-button"]',
              '[data-tour="info-button-kunden"]',
              '[data-tour="info-button-inventory"]'
            ];
            
            let infoButton = null;
            for (const selector of selectors) {
              const elements = document.querySelectorAll(selector);
              for (const el of elements) {
                // Prüfe ob Element sichtbar ist
                const style = window.getComputedStyle(el);
                const rect = el.getBoundingClientRect();
                if (style.display !== 'none' && style.visibility !== 'hidden' && rect.width > 0 && rect.height > 0) {
                  infoButton = el;
                  break;
                }
              }
              if (infoButton) break;
            }
            
            if (!infoButton) {
              // Fallback: Nur dunkler Overlay wenn kein Info-Button gefunden
              return <div className="fixed inset-0 bg-black bg-opacity-75 z-[9998]" />;
            }
            const rect = infoButton.getBoundingClientRect();
            return (
              <>
                {/* Oben */}
                <div
                  className="fixed left-0 right-0 z-[9998] bg-black bg-opacity-75 pointer-events-auto"
                  style={{ top: 0, height: rect.top }}
                />
                {/* Unten */}
                <div
                  className="fixed left-0 right-0 z-[9998] bg-black bg-opacity-75 pointer-events-auto"
                  style={{ top: rect.bottom, bottom: 0 }}
                />
                {/* Links */}
                <div
                  className="fixed z-[9998] bg-black bg-opacity-75 pointer-events-auto"
                  style={{ top: rect.top, left: 0, width: rect.left, height: rect.height }}
                />
                {/* Rechts */}
                <div
                  className="fixed z-[9998] bg-black bg-opacity-75 pointer-events-auto"
                  style={{ top: rect.top, left: rect.right, right: 0, height: rect.height }}
                />
              </>
            );
          })()}
          
          {/* Modal im unteren Bereich */}
          <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-[10001] w-[90%] max-w-md animate-fade-in">
            <div className="bg-white rounded-xl shadow-2xl p-5 border-2 border-emerald-200">
              <div className="text-center">
                <div className="text-4xl mb-3">💡</div>
                <h3 className="font-bold text-lg text-gray-800 mb-2">
                  Tour jederzeit neu starten!
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Klicken Sie auf den <strong className="text-emerald-600">Info-Button</strong> (oben), um die Tour jederzeit erneut zu starten.
                </p>
                <button
                  onClick={closeSkipHint}
                  className="w-full px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg font-semibold hover:opacity-90 transition-all shadow-lg"
                >
                  Verstanden!
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default InteractiveTour;
